import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useBackendAITrading } from '../contexts/BackendAITradingContext';
import { useVidyaHeart } from '../hooks/useVidyaHeart';
import liveChartStream from '../services/liveChartStream';

interface TechnicalSentimentsProps {
  selectedSymbol?: string;
  isRightPanelVisible?: boolean;
}

/** How long the needle rests on Neutral after a candle closes. */
const NEUTRAL_PAUSE_MS = 2500;

/** Ticks are far too frequent to render individually, so their effect is flushed on a timer. */
const TICK_FLUSH_MS = 180;

/**
 * The needle's travel on each side. It stops short of the very end of the arc so there is
 * somewhere left to move, and short of the middle so the side it is on is never ambiguous.
 */
const SIDE_INNER = 40;
const SIDE_OUTER = 6;
const NEUTRAL_VALUE = 50;

/** How far one flush of net ticks pushes the needle, and how strongly it drifts back. */
const TICK_STEP = 0.07;
const TICK_DECAY = 0.05;

const clamp01 = (value: number) => Math.min(1, Math.max(0, value));

const TechnicalSentiments: React.FC<TechnicalSentimentsProps> = ({
  selectedSymbol,
  isRightPanelVisible = false
}) => {
  const { theme } = useTheme();
  const { getAssetAIConfig } = useBackendAITrading();

  // The meter reads the same settings the strategy trades on, so the side it shows and the
  // side the AI takes cannot disagree.
  const strategyConfig = useMemo(
    () => (selectedSymbol ? getAssetAIConfig(selectedSymbol)?.activeStrategyInstance?.config : undefined),
    [selectedSymbol, getAssetAIConfig]
  );

  const heart = useVidyaHeart(selectedSymbol, strategyConfig);

  // Position within the current side: 0 sits next to Neutral, 1 at the far end.
  const [bias, setBias] = useState(0.5);
  const [restingOnNeutral, setRestingOnNeutral] = useState(false);

  const trendRef = useRef<boolean | null>(heart.isTrendUp);
  trendRef.current = heart.isTrendUp;

  const lastTradePriceRef = useRef<number | null>(null);
  const netTicksRef = useRef(0);

  // Pause on Neutral each time a bar closes, then return to whichever side the heart is on.
  useEffect(() => {
    if (!heart.barTime || !heart.ready) return;

    setRestingOnNeutral(true);
    const timer = setTimeout(() => setRestingOnNeutral(false), NEUTRAL_PAUSE_MS);
    return () => clearTimeout(timer);
  }, [heart.barTime, heart.ready]);

  // Live trades nudge the needle around within its side. They are counted here and applied
  // on a timer rather than per trade, because a busy pair prints many every second and each
  // one would otherwise be a React render.
  useEffect(() => {
    if (!selectedSymbol) return;

    lastTradePriceRef.current = null;
    netTicksRef.current = 0;

    const unsubscribe = liveChartStream.subscribe(selectedSymbol, heart.interval, {
      onTrade: trade => {
        const previous = lastTradePriceRef.current;
        lastTradePriceRef.current = trade.price;
        if (previous === null || trade.price === previous) return;
        netTicksRef.current += trade.price > previous ? 1 : -1;
      }
    });

    const flush = setInterval(() => {
      const net = netTicksRef.current;
      netTicksRef.current = 0;

      const trendUp = trendRef.current;
      if (trendUp === null) return;

      setBias(previous => {
        // A tick in the heart's favour pushes toward the far end of its side, one against
        // it eases back toward Neutral. Either way the needle stays on the heart's side.
        const inFavour = trendUp ? net : -net;
        const moved = previous + Math.sign(inFavour) * Math.min(Math.abs(inFavour), 3) * TICK_STEP;
        const next = clamp01(moved + (0.5 - moved) * TICK_DECAY);
        // Returning the previous value lets React skip the render, so a quiet market does
        // not cost one every flush.
        return Math.abs(next - previous) < 0.002 ? previous : next;
      });
    }, TICK_FLUSH_MS);

    return () => {
      unsubscribe();
      clearInterval(flush);
    };
  }, [selectedSymbol, heart.interval]);

  const showNeutral = heart.isTrendUp === null || restingOnNeutral;
  const sentiment: 'Sell' | 'Neutral' | 'Buy' = showNeutral ? 'Neutral' : heart.isTrendUp ? 'Buy' : 'Sell';

  const sentimentValue = useMemo(() => {
    if (showNeutral) return NEUTRAL_VALUE;

    const travel = SIDE_INNER - SIDE_OUTER;
    // Buy mirrors Sell about the centre of the gauge.
    return heart.isTrendUp
      ? 100 - (SIDE_INNER - bias * travel)
      : SIDE_INNER - bias * travel;
  }, [showNeutral, heart.isTrendUp, bias]);

  // The gauge is a half circle and the needle is drawn pointing straight up, so the rotation
  // needed is -90° to reach Sell on the left, 0° for Neutral at the top and +90° for Buy on
  // the right.
  //
  // This previously read `90 - (sentimentValue / 100) * 90`, which spans only 0°..90° — the
  // right-hand half of the gauge. The needle therefore pointed right at 0% and straight up at
  // 100%: inverted, and unable to reach the Sell side at all.
  const needleAngle = (sentimentValue / 100) * 180 - 90;

  // Get color based on sentiment
  const getSentimentColor = () => {
    switch (sentiment) {
      case 'Sell': return '#ef4444'; // red-500
      case 'Neutral': return '#6b7280'; // gray-500
      case 'Buy': return '#22c55e'; // green-500
      default: return '#6b7280';
    }
  };

  const caption = () => {
    if (!selectedSymbol) return 'No symbol selected';
    if (!heart.ready) return `Loading ${heart.interval} candles…`;
    if (heart.isTrendUp === null) return `Waiting for a ${heart.interval} band cross`;
    if (restingOnNeutral) return `${heart.interval} candle closed`;
    return `${heart.isTrendUp ? 'Green' : 'Red'} heart ${heart.level?.toFixed(2)} • ${heart.interval}`;
  };

  return (
    <div className="py-2 pt-0">
      <div className={`theme-bg-secondary bg-opacity-95 p-3 py-3 rounded-lg z-10 shadow-xs relative ${theme === 'light' ? 'border-gray-200' : 'border-gray-800'}`}>
        {/* Header */}
        {/* Half Circle Gauge */}
        <div className="relative w-full h-36 flex justify-center items-center mb-0">
          <svg width="200" height="100" className="absolute top-[15px]">
            {/* Define gradient */}
            <defs>
              <linearGradient id="sentimentGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#ef4444" />
                <stop offset="50%" stopColor="#6b7280" />
                <stop offset="100%" stopColor="#22c55e" />
              </linearGradient>
            </defs>
            
            {/* Background arc */}
            <path
              d="M 30 85 A 70 70 0 0 1 170 85"
              fill="none"
              stroke={theme === 'dark' ? '#374151' : '#e5e7eb'}
              strokeWidth="6"
              strokeLinecap="round"
            />
            
            {/* Gradient arc from red (left) to green (right) */}
            <path
              d="M 30 85 A 70 70 0 0 1 170 85"
              fill="none"
              stroke="#6b7280"
              strokeWidth="6"
              strokeLinecap="round"
              style={{
                stroke: 'url(#sentimentGradient)',
                strokeWidth: '6',
                strokeLinecap: 'round'
              }}
            />
            
            {/* Needle. Pivots on (100, 85), the centre of the arc above, so the tip sweeps
                parallel to it. It used to pivot on (100, 80), five pixels off centre, which
                made the sweep lopsided. */}
            <g
              transform={`rotate(${needleAngle} 100 85)`}
              style={{
                // Swinging to and from Neutral is a deliberate move and reads better slowly;
                // the tick jitter is continuous and would lag badly at the same duration.
                transition: `transform ${restingOnNeutral ? 700 : 260}ms ease-out`
              }}
            >
              <line
                x1="100"
                y1="85"
                x2="100"
                y2="38"
                stroke={getSentimentColor()}
                strokeWidth="2"
                strokeLinecap="round"
              />
              <circle
                cx="100"
                cy="85"
                r="3"
                fill={getSentimentColor()}
              />
            </g>
          </svg>

          {/* Three labels positioned around the perfect 180-degree circle */}
          {/* Sell at 180° (left) */}
          <div className="absolute left-2 bottom-2">
            <div className="text-xs font-medium text-gray-500">Sell</div>
          </div>
          
          {/* Neutral at 90° (top) */}
          <div className="absolute left-1/2 transform -translate-x-1/2 top-0">
            <div className="text-xs font-medium text-gray-500">Neutral</div>
          </div>
          
          {/* Buy at 0° (right) */}
          <div className="absolute right-2 bottom-2">
            <div className="text-xs font-medium text-gray-500">Buy</div>
          </div>
        </div>

        {/* Current Sentiment */}
        <div className="text-center -mt-5">
          <div 
            className="text-xs font-bold"
            style={{ color: getSentimentColor() }}
          >
            {sentiment}
          </div>
          <div className="text-xs theme-text-quaternary mt-1">
            {caption()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TechnicalSentiments;
